import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useGraphql } from 'utils/hooks/useGraphql';

import { fetchWorkflowWebhooksQuery } from 'api/queries';
import { FullWidth } from 'ui/utility/fullWidth';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import { Search, Delete } from 'ui/icons';
import { PAGE_SIZES, PaginationFooter } from 'ui/paginationFooter';
import { DeleteItemsModal } from 'ui/indexPages/modals/DeleteItemsModal';
import { BulkActionsBar } from 'ui/bulkActionsBar';
import { useGroupSelection } from 'utils/hooks/useGroupSelection';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { prefixWithUserProject } from 'utils/indexPageUtils';
import { deleteWorkflowWebhookRequest } from 'modules/workflowWebhooks';
import { CopyWorkflowWebhookModal } from './components/CopyWorkflowWebhookModal';
import { WorkflowWebhookIndexHeader } from './WorkflowWebhookIndexHeader';
import { WorkflowWebhookIndexTable } from './WorkflowWebhookIndexTable';
import { SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS, SORT_BY_FIELD, SORT_DIRECTIONS } from './constants';
import { RightMeta, StyledMeta, WideInputText } from './WorkflowWebhookIndexPage.styles';
// import { PageInfo } from './types';
import { deleteWorkflowWebhooksRequest } from './actions';

export const displayContent = {
  clear: 'Clear',
  searchPlaceholder: 'Search webhooks',
};

const displayTextForDeletingWorkflowWebhoks = {
  actionName: {
    verb: 'delete',
    adjective: 'deleted',
  },
  itemTypeName: {
    singular: 'webhook',
    plural: 'webhooks',
  },
  suffixText: 'This cannot be undone.',
  actionIcon: Delete,
};

export const WorkflowWebhookIndexPage = () => {
  const [[showCopyModal, workflowWebhookToCopy], setShowCopyModal] = useState([false, null]);
  const [[showDeleteModal, workflowWebhooksToDelete], setShowDeleteModal] = useState([false, []]);
  const [currentPageSize, setCurrentPageSize] = useState(PAGE_SIZES[1]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useLocalStorage(
    prefixWithUserProject('workflow-webhook-index-sort-by'),
    SORT_BY_FIELD.UPDATED_AT,
  );
  const [sortDirection, setSortDirection] = useLocalStorage(
    prefixWithUserProject('workflow-webhook-index-sort-direction'),
    SORT_DIRECTIONS.DESCENDING,
  );

  const [savedSearch, setSavedSearch] = useLocalStorage(
    prefixWithUserProject('workflow-webhook-index-index-search'),
    '',
  );
  const [searchInputValue, setSearchInputValue] = useState(savedSearch);

  // On debounced changes to `searchInputValue`, save the search to local storage and trigger a GraphQL refetch
  useDebouncedEffect(() => setSavedSearch(searchInputValue), [searchInputValue], {
    skipEffectOnMount: true,
    debounceTimeout: SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS,
  });

  const dispatch = useDispatch();

  /**
   Selection-related variables and functions
   */
  const {
    selected,
    selectByIds: selectMultiple,
    toggleSelection,
    deselectAll,
  } = useGroupSelection();

  const selectedWorkflowWebhooksCount = selected.length;
  const showBulkActionBar = selectedWorkflowWebhooksCount > 0;

  /**
    GraphQL
   */
  const {
    data = {},
    loading,
    refetch,
  } = useGraphql(fetchWorkflowWebhooksQuery, {
    notifyOnNetworkStatusChange: true, // allow loading state update on refetch
    variables: {
      pagination: {
        limit: currentPageSize,
        offset: (currentPage - 1) * currentPageSize,
      },
      search: savedSearch || null,
      sort: {
        sortBy,
        sortDirection,
      },
    },
  });

  const {
    info = {},
    results: workflowWebhooks = [],
  } /* : { info: PageInfo, workflowWebhooks: WorkflowWebhook[] } */ = data?.workflowWebhooks || {};

  const bulkActions = [
    {
      label: 'Delete',
      onClick: () =>
        setShowDeleteModal([true, workflowWebhooks.filter((w) => selected.includes(w.id))]),
      sortIndex: 2,
    },
  ];

  const handlePaginationUpdate = ({ page, pageSize }) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  const onDeleted = () => {
    deselectAll();
    refetch();
  };

  const handleConfirmDelete = () => {
    if (workflowWebhooksToDelete.length > 1) {
      dispatch(deleteWorkflowWebhooksRequest(workflowWebhooksToDelete, onDeleted));
    } else if (workflowWebhooksToDelete.length === 1) {
      dispatch(deleteWorkflowWebhookRequest(workflowWebhooksToDelete[0], onDeleted));
    }

    setShowDeleteModal([false, []]);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [savedSearch]);

  // Refetch whenever a graphQL variable changes
  useEffect(() => {
    refetch();
  }, [sortBy, sortDirection, currentPageSize]);

  // Show pagination only when table has items or is dislaying no search results
  const showPagination = workflowWebhooks.length > 0;

  const affectedWorkflowNodeIds = new Set();
  const affectedWorkflowIds = new Set();

  workflowWebhooksToDelete.forEach((w) => {
    w.workflowNodes?.forEach((n) => {
      affectedWorkflowNodeIds.add(n.id);
      affectedWorkflowIds.add(n.workflow.id);
    });
  });

  // selected.forEach(id => {
  //   // eslint-disable-next-line
  //   if(!workflowWebhooks.find(w => w.id == id)) {
  //     toggleSelection(id);
  //   }
  // });

  return (
    <div data-test="workflow-webhooks-index-page">
      <WorkflowWebhookIndexHeader />
      {!showBulkActionBar && (
        <StyledMeta>
          <RightMeta>
            <WideInputText
              autoComplete="off"
              data-test="workflow-webhook-search-input"
              id="workflow-webhook-search-input"
              onChange={setSearchInputValue}
              placeholder={displayContent.searchPlaceholder}
              Prefix={<Search />}
              value={searchInputValue}
            />
          </RightMeta>
        </StyledMeta>
      )}
      {showCopyModal && (
        <CopyWorkflowWebhookModal
          workflowWebhook={workflowWebhookToCopy}
          onClose={() => setShowCopyModal([false, null])}
          refetch={refetch}
          toggleSelection={toggleSelection}
        />
      )}
      {showDeleteModal && (
        <DeleteItemsModal
          onConfirm={handleConfirmDelete}
          onClose={() => {
            if (workflowWebhooksToDelete.length > 0) {
              setShowDeleteModal([false, []]);
            }
          }}
          content={workflowWebhooksToDelete}
          displayTextForContentItems={displayTextForDeletingWorkflowWebhoks}
          warning={
            affectedWorkflowNodeIds.size > 0 ? (
              <Banner variant={BannerVariants.WARNING}>
                <b>{affectedWorkflowNodeIds.size}</b> workflow node
                {affectedWorkflowNodeIds.size === 1 ? '' : 's'} (in{' '}
                <b>{affectedWorkflowIds.size}</b> workflow
                {affectedWorkflowIds.size === 1 ? '' : 's'}) will no longer be able to call this
                webhook. Consider updating your workflow{affectedWorkflowIds.size === 1 ? '' : 's'}{' '}
                before deleting the webhook.
              </Banner>
            ) : null
          }
        />
      )}
      <FullWidth>
        {showBulkActionBar && (
          <BulkActionsBar actions={bulkActions} numSelected={selectedWorkflowWebhooksCount} />
        )}
        <WorkflowWebhookIndexTable
          workflowWebhooks={workflowWebhooks}
          sortBy={sortBy}
          sortDirection={sortDirection}
          setSortBy={(newSortBy) => setSortBy(newSortBy)}
          setSortDirection={setSortDirection}
          selected={selected}
          selectAll={() => selectMultiple(workflowWebhooks.map((wh) => wh.id))}
          refetch={refetch}
          isLoading={loading}
          hasFilters={searchInputValue.length > 0}
          setShowCopyModal={setShowCopyModal}
          setShowDeleteModal={setShowDeleteModal}
          toggleSelection={toggleSelection}
          deselectAll={deselectAll}
        />
        {showPagination && (
          <PaginationFooter
            currentPage={info.page}
            itemsPerPage={info.limit}
            numItems={info.count}
            pageSizes={PAGE_SIZES}
            onUpdate={handlePaginationUpdate}
          />
        )}
      </FullWidth>
    </div>
  );
};
